import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
	SqlPlaygroundService,
	FileTypeDescriptor,
	QueryExecutionResult
} from './sql-playground.service';
import { FileApiService } from '@angular-workspace/services';

interface VersionFile {
	name: string;
	size?: string;
	lastUpdated?: string;
	license?: string;
	isSelected?: boolean;
}

/** File extensions allowed in SQL Playground (JSON, CSV, TSV, Parquet only) */
const SQL_PLAYGROUND_EXTENSIONS = ['json', 'csv', 'tsv', 'parquet'];

@Component({
	selector: 'app-sql-playground',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './sql-playground.component.html',
	styleUrls: ['./sql-playground.component.scss']
})
export class SqlPlaygroundComponent implements OnInit, OnDestroy, OnChanges {
	@Input() datasetId: string | null = null;
	@Input() versionFiles: VersionFile[] = [];
	@Input() versionFilesTotalSize: string = '';
	@Input() versionFilesLastUpdated: string = '';
	@Input() license: string = '';

	/** Effective file list: from parent or lazy-loaded via FileApiService */
	displayFiles: VersionFile[] = [];
	displayFilesTotalSize = '';
	displayFilesLastUpdated = '';
	loadingFiles = false;
	filesError: string | null = null;

	private fileApiService = inject(FileApiService);
	private cdr = inject(ChangeDetectorRef);
	sql = '';
	editorHeight = 180;
	editorFontSize = 14; // Default font size in pixels
	highlightedSql: SafeHtml = '';
	isRunning = false;
	runStatus: 'idle' | 'running' | 'success' | 'error' = 'idle';
	lastRunAt: Date | null = null;
	executionTimeMs: number | null = null;
	resultColumns: string[] = [];
	resultRows: Record<string, unknown>[] = [];
	errorMessage: string | null = null;

	supportedFileTypes: FileTypeDescriptor[] = [];
	fileInputAccept = '';
	isUploading = false;
	uploadError: string | null = null;
	uploadedTableName: string | null = null;
	uploadedRowCount: number | null = null;
	lastUploadedFileName: string | null = null;
	
	// Custom uploaded files
	customFiles: Array<{
		name: string;
		size: string;
		isSelected: boolean;
		tableName?: string;
	}> = [];
	
	// Currently active files for querying (multiple selection)
	activeTableNames: string[] = [];
	activeFileNames: string[] = [];
	
	// File details toggle (one open at a time; key is 'version:fileName' or 'custom:fileName')
	expandedFileDetails: string | null = null;

	// Version Files section header tooltip
	versionFilesTooltipVisible = false;
	versionFilesTooltipTop = 0;
	versionFilesTooltipLeft = 0;

	get canResetPlayground(): boolean {
		return (
			!!this.sql?.trim() ||
			this.runStatus !== 'idle' ||
			this.resultRows.length > 0 ||
			this.resultColumns.length > 0 ||
			this.errorMessage !== null ||
			this.uploadError !== null ||
			this.executionTimeMs !== null ||
			this.lastRunAt !== null
		);
	}

	get selectedFileName(): string {
		// Return active file names if available
		if (this.activeFileNames.length > 0) {
			if (this.activeFileNames.length === 1) {
				return this.activeFileNames[0];
			}
			return `${this.activeFileNames.length} files selected`;
		}
		
		// Fallback to selected display files (from API)
		const selectedDisplayFiles = this.displayFiles?.filter(file => file.isSelected) || [];
		if (selectedDisplayFiles.length > 0) {
			if (selectedDisplayFiles.length === 1) {
				return selectedDisplayFiles[0].name;
			}
			return `${selectedDisplayFiles.length} files selected`;
		}
		
		// Fallback to first file if available
		if (this.displayFiles && this.displayFiles.length > 0) {
			return this.displayFiles[0].name;
		}
		
		// Default fallback
		return 'SQL Playground';
	}
	
	get activeTableNamesForQuery(): string[] {
		return this.activeTableNames.length > 0 ? this.activeTableNames : [];
	}
	
	get hasSelectedFiles(): boolean {
		return this.activeTableNames.length > 0 || 
		       this.displayFiles.some(f => f.isSelected) || 
		       this.customFiles.some(f => f.isSelected);
	}

	constructor(
		private readonly sqlPlaygroundService: SqlPlaygroundService,
		private readonly sanitizer: DomSanitizer
	) {}

	ngOnInit(): void {
		// Initialize supported file types
		this.supportedFileTypes = this.sqlPlaygroundService.getSupportedFileTypes();
		this.fileInputAccept = this.supportedFileTypes
			.flatMap((type) => type.extensions.map((ext) => `.${ext}`))
			.join(',');
		
		// Initialize with sample query (will be updated when file is selected)
		this.sql = '';
		this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
		
		this.syncDisplayFiles();
		if (this.displayFiles.length > 0) {
			// Use files from parent
		} else if (this.datasetId) {
			this.loadFilesFromApi();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['versionFiles'] || changes['versionFilesTotalSize'] || changes['versionFilesLastUpdated']) {
			this.syncDisplayFiles();
		}
		if (changes['datasetId'] && this.datasetId && this.displayFiles.length === 0) {
			this.loadFilesFromApi();
		}
	}

	private isSqlPlaygroundFile(fileName: string): boolean {
		const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
		return SQL_PLAYGROUND_EXTENSIONS.includes(ext);
	}

	private syncDisplayFiles(): void {
		if (this.versionFiles?.length > 0) {
			const filtered = this.versionFiles.filter(f => this.isSqlPlaygroundFile(f.name));
			this.displayFiles = filtered.map(f => ({ ...f }));
			this.displayFilesTotalSize = this.versionFilesTotalSize;
			this.displayFilesLastUpdated = this.versionFilesLastUpdated;
		}
	}

	private async loadFilesFromApi(): Promise<void> {
		if (!this.datasetId) return;
		this.loadingFiles = true;
		this.filesError = null;
		this.cdr.markForCheck();

		try {
			const response = await this.fileApiService.getFilesForPreview(this.datasetId);
			const apiFiles = this.extractFilesFromResponse(response?.data) as Array<{
				key?: string;
				size?: number;
				lastModified?: string;
				isFile?: boolean;
			}>;

			if (apiFiles.length > 0) {
				const filtered = apiFiles.filter(
					(f) => f.isFile !== false && this.isSqlPlaygroundFile(f.key || '')
				);
				let totalSizeBytes = 0;
				let latestModified: Date | null = null;

				for (const f of filtered) {
					totalSizeBytes += f.size || 0;
					if (f.lastModified) {
						const d = new Date(f.lastModified);
						if (!latestModified || d > latestModified) latestModified = d;
					}
				}

				this.displayFiles = filtered.map((f, i) => ({
					name: f.key || `file_${i + 1}`,
					size: (f.size || 0) > 0 ? this.formatFileSize(f.size || 0) : undefined,
					lastUpdated: f.lastModified,
					license: this.license,
					isSelected: i === 0
				}));

				this.displayFilesTotalSize = totalSizeBytes > 0 ? this.formatFileSize(totalSizeBytes) : '';
				this.displayFilesLastUpdated = latestModified ? latestModified.toLocaleDateString() : '';
			} else {
				this.displayFiles = [];
				this.displayFilesTotalSize = '';
				this.displayFilesLastUpdated = '';
			}
		} catch (err: unknown) {
			this.filesError = err instanceof Error ? err.message : 'Failed to load files';
			this.displayFiles = [];
			console.error('[SQL Playground] Error loading files:', err);
		} finally {
			this.loadingFiles = false;
			this.cdr.markForCheck();
		}
	}

	private extractFilesFromResponse(data: unknown): unknown[] {
		if (!data || typeof data !== 'object') return [];
		const obj = data as Record<string, unknown>;
		if (Array.isArray(obj['files'])) return obj['files'];
		const inner = obj['data'] as Record<string, unknown> | undefined;
		if (inner && typeof inner === 'object' && Array.isArray(inner['files'])) return inner['files'];
		return [];
	}

	ngOnDestroy(): void {
		// Cleanup if needed
	}

	async runQuery(): Promise<void> {
		if (this.isRunning) {
			return;
		}
		this.isRunning = true;
		this.runStatus = 'running';
		this.executionTimeMs = null;
		this.resultColumns = [];
		this.resultRows = [];
		this.errorMessage = null;

		try {
			const result = await this.runDuckdbQuery(this.sql);
			this.applySuccessResult(result);
		} catch (error) {
			this.handleRunError(error);
		} finally {
			this.isRunning = false;
		}
	}

	resetPlayground(): void {
		if (this.isRunning) {
			return;
		}

		this.sql = '';
		this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
		this.runStatus = 'idle';
		this.lastRunAt = null;
		this.executionTimeMs = null;
		this.resultColumns = [];
		this.resultRows = [];
		this.errorMessage = null;
		this.uploadError = null;
		// Note: We don't clear activeTableNames/activeFileNames here to keep files selected
		// If you want to clear selection on reset, uncomment the next lines:
		// this.activeTableNames = [];
		// this.activeFileNames = [];
	}

	async onFileSelected(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		const file = input.files?.item(0);

		if (!file || this.isUploading) {
			return;
		}

		this.isUploading = true;
		this.uploadError = null;

		try {
			// Generate table name from file name for better UX
			const tableName = this.generateTableNameFromFileName(file.name);
			
			const { tableName: loadedTableName, rowCount, fileName } =
				await this.sqlPlaygroundService.loadFile(file, tableName);
			this.uploadedTableName = loadedTableName;
			this.uploadedRowCount = rowCount;
			this.lastUploadedFileName = fileName;

			// Add to custom files list
			const fileSize = this.formatFileSize(file.size);
			const customFile = {
				name: file.name,
				size: fileSize,
				isSelected: true,
				tableName: loadedTableName
			};
			
			// Add to custom files (auto-select it)
			this.customFiles.push(customFile);
			
			// Add to active files
			if (!this.activeTableNames.includes(loadedTableName)) {
				this.activeTableNames.push(loadedTableName);
			}
			if (!this.activeFileNames.includes(file.name)) {
				this.activeFileNames.push(file.name);
			}

			// Update query with uploaded table(s)
			this.updateQueryForActiveTables();

			// Trigger change detection so the new file appears immediately in the Custom Files list
			this.cdr.detectChanges();

			// Auto-run query after upload
			await this.runQuery();
		} catch (error) {
			this.uploadError = this.extractErrorMessage(error);
		} finally {
			this.isUploading = false;
			if (input) {
				input.value = '';
			}
			this.cdr.detectChanges();
		}
	}

	private formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
	}

	generateTableNameFromFileName(fileName: string): string {
		// Remove extension and sanitize the file name
		const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
		// Replace invalid characters with underscores and make lowercase
		const sanitized = nameWithoutExt
			.replace(/[^a-zA-Z0-9_]/g, '_')
			.toLowerCase()
			.replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
			.replace(/_+/g, '_'); // Replace multiple underscores with single
		
		// Ensure it's a valid identifier (not empty, doesn't start with number)
		if (!sanitized || /^\d/.test(sanitized)) {
			return 'uploaded_data';
		}
		
		return sanitized || 'uploaded_data';
	}

	onCustomFileToggle(file: { name: string; size: string; isSelected: boolean; tableName?: string }): void {
		// Toggle selection
		file.isSelected = !file.isSelected;
		
		if (file.isSelected && file.tableName) {
			// Add to active files
			if (!this.activeTableNames.includes(file.tableName)) {
				this.activeTableNames.push(file.tableName);
			}
			if (!this.activeFileNames.includes(file.name)) {
				this.activeFileNames.push(file.name);
			}
		} else {
			// Remove from active files
			if (file.tableName) {
				this.activeTableNames = this.activeTableNames.filter(t => t !== file.tableName);
			}
			this.activeFileNames = this.activeFileNames.filter(n => n !== file.name);
		}
		
		// Update query
		this.updateQueryForActiveTables();
	}

	removeCustomFile(file: { name: string; tableName?: string }): void {
		this.customFiles = this.customFiles.filter(f => f.name !== file.name);
		if (file.tableName) {
			this.activeTableNames = this.activeTableNames.filter(t => t !== file.tableName);
		}
		this.activeFileNames = this.activeFileNames.filter(n => n !== file.name);
		this.updateQueryForActiveTables();

		// Clear the query results preview
		this.runStatus = 'idle';
		this.lastRunAt = null;
		this.executionTimeMs = null;
		this.resultColumns = [];
		this.resultRows = [];
		this.errorMessage = null;

		this.cdr.markForCheck();
	}

	private async runDuckdbQuery(sql: string): Promise<QueryExecutionResult> {
		return this.sqlPlaygroundService.executeQuery(sql);
	}

	private applySuccessResult(result: QueryExecutionResult): void {
		this.resultRows = result.rows;
		this.resultColumns = result.columns;
		this.executionTimeMs = result.executionTimeMs;
		this.lastRunAt = new Date();
		this.runStatus = 'success';
	}

	private handleRunError(error: unknown): void {
		this.runStatus = 'error';
		this.executionTimeMs = null;
		this.resultRows = [];
		this.resultColumns = [];
		this.errorMessage = this.extractErrorMessage(error);

		console.error('[SQL Playground] Query execution failed:', error);
	}

	async onFileCardClick(file: VersionFile): Promise<void> {
		// Toggle selection (allow multiple)
		file.isSelected = !file.isSelected;
		
		// Version files from API are display-only; they require fetch+load to be queryable.
		// For now, track selection in activeFileNames. Table names come from custom uploaded files.
		if (file.isSelected) {
			if (!this.activeFileNames.includes(file.name)) {
				this.activeFileNames.push(file.name);
			}
		} else {
			this.activeFileNames = this.activeFileNames.filter(n => n !== file.name);
		}
		
		this.updateQueryForActiveTables();
	}
	
	private updateQueryForActiveTables(): void {
		const tables = this.activeTableNames.filter(t => t); // Filter out null/undefined
		
		if (tables.length === 0) {
			this.sql = '';
			this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
			return;
		}
		
		if (tables.length === 1) {
			this.sql = `SELECT * FROM ${tables[0]} LIMIT 20;`;
		} else {
			// Multiple tables - use CROSS JOIN
			let query = `SELECT * FROM ${tables[0]}`;
			for (let i = 1; i < tables.length; i++) {
				query += `\nCROSS JOIN ${tables[i]}`;
			}
			query += `\nLIMIT 20;`;
			this.sql = query;
		}
		this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
	}

	private extractErrorMessage(error: unknown): string {
		if (!error) {
			return 'An unknown error occurred while executing the query.';
		}

		if (error instanceof Error) {
			return (
				error.message ||
				'An unexpected error occurred while executing the query.'
			);
		}

		if (typeof error === 'string') {
			return error;
		}

		try {
			return JSON.stringify(error);
		} catch {
			return 'An unexpected error occurred while executing the query.';
		}
	}
	
	toggleFileDetails(fileName: string, context: 'version' | 'custom'): void {
		const key = `${context}:${fileName}`;
		this.expandedFileDetails = this.expandedFileDetails === key ? null : key;
	}

	isFileDetailsExpanded(fileName: string, context: 'version' | 'custom'): boolean {
		return this.expandedFileDetails === `${context}:${fileName}`;
	}

	showVersionFilesTooltip(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		const rect = target.getBoundingClientRect();
		this.versionFilesTooltipTop = rect.bottom + 8;
		this.versionFilesTooltipLeft = rect.left + rect.width / 2;
		this.versionFilesTooltipVisible = true;
	}

	hideVersionFilesTooltip(): void {
		this.versionFilesTooltipVisible = false;
	}
	
	increaseFontSize(): void {
		if (this.editorFontSize < 24) {
			this.editorFontSize += 2;
		}
	}
	
	decreaseFontSize(): void {
		if (this.editorFontSize > 10) {
			this.editorFontSize -= 2;
		}
	}
	
	onSqlInput(event: Event): void {
		const textarea = event.target as HTMLTextAreaElement;
		// Update highlighted SQL
		setTimeout(() => {
			this.highlightedSql = this.sanitizer.bypassSecurityTrustHtml(this.highlightSql(this.sql));
		}, 0);
		// Sync scroll
		const highlightElement = textarea.parentElement?.querySelector('.sql-playground__editor-highlight') as HTMLElement;
		if (highlightElement) {
			highlightElement.scrollTop = textarea.scrollTop;
			highlightElement.scrollLeft = textarea.scrollLeft;
		}
	}
	
	onSqlScroll(event: Event): void {
		const textarea = event.target as HTMLTextAreaElement;
		const highlightElement = textarea.parentElement?.querySelector('.sql-playground__editor-highlight') as HTMLElement;
		if (highlightElement) {
			highlightElement.scrollTop = textarea.scrollTop;
			highlightElement.scrollLeft = textarea.scrollLeft;
		}
	}
	
	highlightSql(sql: string): string {
		if (!sql) return '';
		
		// Escape HTML first
		let highlighted = sql
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
		
		// Highlight comments first (they should not be processed further)
		highlighted = highlighted.replace(/--.*$/gm, '<span class="sql-comment">$&</span>');
		highlighted = highlighted.replace(/\/\*[\s\S]*?\*\//g, '<span class="sql-comment">$&</span>');
		
		// Highlight string literals (before keywords to avoid conflicts)
		highlighted = highlighted.replace(/'([^']*)'/g, '<span class="sql-string">\'$1\'</span>');
		highlighted = highlighted.replace(/"([^"]*)"/g, '<span class="sql-string">"$1"</span>');
		
		// SQL keywords to highlight (order matters - longer keywords first)
		const keywords = [
			'UNION ALL', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
			'ORDER BY', 'GROUP BY', 'IS NOT NULL', 'IS NULL',
			'SELECT', 'FROM', 'WHERE', 'HAVING',
			'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'ALTER', 'DROP',
			'TABLE', 'INDEX', 'VIEW', 'DATABASE', 'SCHEMA',
			'JOIN', 'ON', 'AS', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
			'UNION', 'INTERSECT', 'EXCEPT',
			'DISTINCT', 'ALL', 'TOP', 'LIMIT', 'OFFSET',
			'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
			'COUNT', 'SUM', 'AVG', 'MAX', 'MIN',
			'ASC', 'DESC', 'NULL',
			'EXISTS', 'ANY', 'SOME'
		];
		
		// Highlight keywords (case-insensitive, but only if not already inside a span)
		keywords.forEach(keyword => {
			const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`\\b(${escapedKeyword.replace(/\s+/g, '\\s+')})\\b(?![^<]*>)`, 'gi');
			highlighted = highlighted.replace(regex, '<span class="sql-keyword">$1</span>');
		});
		
		// Highlight numbers (only if not already inside a span)
		highlighted = highlighted.replace(/\b(\d+\.?\d*)\b(?![^<]*>)/g, '<span class="sql-number">$1</span>');
		
		return highlighted;
	}
}
